// Microcopy for the SAC (Success Analytics Component) information tooltips.
// One entry per analytics surface. Kept as data so copy can be tuned without
// touching components. Every field is short, plain, and board-exam-oriented.

export interface SacInfo {
  /** Human title of the metric (matches or clarifies the card title). */
  name: string;
  /** What it measures. */
  measures: string;
  /** Why it matters for learning / boards. */
  matters: string;
  /** How a student improves it. */
  improve: string;
  /** What a good score / state looks like. */
  goodScore: string;
}

export type SacKey =
  | 'streak'
  | 'todaysSet'
  | 'wateringGoal'
  | 'fiveSacks'
  | 'goGreen'
  | 'weeksBest'
  | 'concepts'
  | 'confidenceTimeline'
  | 'confidenceBands'
  | 'effort'
  | 'progressOverTime'
  // Per-sack (mastery bin) explanations.
  | 'binPink'
  | 'binOrange'
  | 'binBlue'
  | 'binPurple'
  | 'binGreen';

export const SAC_INFO: Record<SacKey, SacInfo> = {
  streak: {
    name: 'Daily streak',
    measures: 'The number of days in a row you’ve practised at least once.',
    matters: 'Boards reward steady revision far more than occasional cramming. A streak is proof you’re building the daily habit that compounds.',
    improve: 'Clear even one small set every day — consistency beats intensity. A freeze covers the odd missed day so a good run survives.',
    goodScore: 'Any unbroken streak is a win; 7+ days means the habit is taking root.',
  },
  todaysSet: {
    name: 'Today’s set',
    measures: 'A short, mixed batch of questions chosen for you across your weaker and due concepts.',
    matters: 'Mixed daily practice (interleaving) is one of the most exam-effective study methods — it trains you to pick the right method, like a real paper.',
    improve: 'Finish the set in one focused sitting rather than skipping around. Quality attempts matter more than speed.',
    goodScore: 'Completing today’s set daily keeps every concept moving toward Green.',
  },
  wateringGoal: {
    name: 'Daily watering goal',
    measures: 'Your practice effort today (XP) against a small daily target.',
    matters: 'A reachable daily target keeps momentum without burnout — the point is to show up, not to score.',
    improve: 'Hit the goal with honest attempts; the XP follows the work, so focus on the questions, not the number.',
    goodScore: 'Reaching 100% most days is plenty — overshooting isn’t the aim.',
  },
  fiveSacks: {
    name: 'Your 5 sacks (mastery bins)',
    measures: 'Every concept you’ve attempted, sorted into five colours from Pink (needs replanting) to Green (ready to harvest).',
    matters: 'It turns a huge syllabus into a clear, honest map of where you stand — so revision time goes where it actually helps.',
    improve: 'Work Pink and Orange concepts first; re-attempt them until they climb toward Green.',
    goodScore: 'A bank heavy in Green and Purple, with Pink shrinking week over week.',
  },
  goGreen: {
    name: 'Go-Green nudge',
    measures: 'Concepts sitting in Blue — one solid review away from Green (mastered).',
    matters: 'These are your fastest wins: a little effort converts “nearly there” into confirmed mastery before the exam.',
    improve: 'Review the listed Blue cards and confirm them when you can recall the method unaided.',
    goodScore: 'Clearing your Blue queue regularly keeps mastery flowing into Green.',
  },
  weeksBest: {
    name: 'This week’s best',
    measures: 'How many concepts you’ve grown to Green in the last seven days.',
    matters: 'Visible weekly progress is motivating and a reliable signal that your revision is sticking, not just being seen.',
    improve: 'Convert a couple of Blue/Purple concepts to Green each week — small, repeatable gains.',
    goodScore: 'A steady positive number every week is exactly what board readiness looks like.',
  },
  concepts: {
    name: 'Your concepts board',
    measures: 'The full curriculum laid out by mastery bin, with every card you can review or grow.',
    matters: 'It’s your complete revision control panel — nothing in the syllabus is hidden, so coverage gaps can’t surprise you in the exam.',
    improve: 'Drag a card you can now solve unaided into Green; revisit weaker columns first.',
    goodScore: 'Strong coverage across topics with most cards in the upper (greener) bins.',
  },
  confidenceTimeline: {
    name: 'Confidence timeline',
    measures: 'How your topic-level confidence band has moved across your recent papers.',
    matters: 'Trends matter more than any single score — an upward line shows real, durable improvement over time.',
    improve: 'Keep sitting papers and clearing weak concepts; the line rises as mastery accumulates.',
    goodScore: 'A line that trends upward or holds in the higher bands across papers.',
  },
  confidenceBands: {
    name: 'Confidence bands',
    measures: 'Your overall confidence per topic (Green / Blue / Orange-Pink), once you’ve sat enough papers.',
    matters: 'It tells you which topics you can rely on under exam pressure and which still need work.',
    improve: 'Target Orange-Pink topics with focused practice and re-sit papers to lift the band.',
    goodScore: 'Most topics in Green or Blue, with Orange-Pink reserved for genuine weak spots.',
  },
  effort: {
    name: 'Effort',
    measures: 'Time on task and how regularly you study — the work behind the scores.',
    matters: 'Effort is the one input you fully control. Consistent, deliberate practice is the strongest predictor of board outcomes.',
    improve: 'Aim for regular shorter sessions over rare long ones; protect a daily study slot.',
    goodScore: 'A steady weekly rhythm of focused sessions — regularity over marathon days.',
  },
  progressOverTime: {
    name: 'Progress over time',
    measures: 'Your mastery and scores trending across all the papers you’ve taken.',
    matters: 'Seeing the trajectory keeps you motivated and confirms whether your current routine is working.',
    improve: 'Stick to the loop — daily set, clear weak concepts, sit papers — and the trend takes care of itself.',
    goodScore: 'A consistently rising or stable-high trend as exams approach.',
  },

  // ── The five mastery bins (sacks), lowest → highest ──
  binPink: {
    name: 'Pink sack — replant',
    measures: 'Concepts you couldn’t crack yet — they need re-teaching from the basics.',
    matters: 'Ignored Pinks become lost exam marks, and they quietly hold back the topics built on top of them.',
    improve: 'Use the scaffold and worked solutions to rebuild the fundamentals, then re-attempt.',
    goodScore: 'A near-empty Pink sack — every concept has at least started to sprout.',
  },
  binOrange: {
    name: 'Orange sack — needs work',
    measures: 'Concepts you struggled with — wrong attempts before getting there, or a shaky method.',
    matters: 'Your highest-value revision targets: a little effort here moves your score the most.',
    improve: 'Re-learn the method, then drill similar questions until it feels routine.',
    goodScore: 'This sack thinning out as concepts climb into Blue and beyond.',
  },
  binBlue: {
    name: 'Blue sack — growing',
    measures: 'Concepts you got right with a little support (a hint or an extra attempt). Understanding is forming.',
    matters: 'Solidly “getting there.” Targeted review turns these into reliable, unaided marks.',
    improve: 'Practise without the hint — when you can solve it solo, it climbs to Purple/Green.',
    goodScore: 'A shrinking Blue sack means concepts are maturing into mastery.',
  },
  binPurple: {
    name: 'Purple sack — almost ripe',
    measures: 'Correct, but needing a bit more time or a second look than a clean first solve.',
    matters: 'One confident review away from Green — among your fastest routes to more banked marks.',
    improve: 'Re-attempt until you can solve them quickly and unaided; then they turn Green.',
    goodScore: 'A healthy Purple sack feeding steadily into Green.',
  },
  binGreen: {
    name: 'Green sack — ready to harvest',
    measures: 'Concepts you answered correctly, first try and on time — confirmed mastery.',
    matters: 'These are your banked marks. Walking into the exam, you want as many concepts here as possible.',
    improve: 'Keep them green with the occasional spaced review so they don’t fade before the exam.',
    goodScore: 'A large, steadily-growing Green sack — your exam-ready core.',
  },
};
